/**
 * Una sola regla de «què li falta a aquest viatger per als Mossos» (ARQ-8, sprint 7).
 *
 * `mossosTravellerIssues(viatger, avui)` és pura i torna **codis**: el text el posa qui pinta
 * (la pujada, `/mossos`, el formulari públic), cadascú en el seu idioma.
 *
 * La `severity` diu què fa avui la pujada automàtica: `error` exclou el viatger del fitxer
 * (`isBlockingForPortal`), `warning` no. Els altres llocs que la criden encara trien quins codis
 * miren, perquè fins ara no deien el mateix i aquest pas no canvia cap resultat; les diferències
 * que queden són a la taula d'ARQ-8 de `plans/fitxes/20261008-sprint-7-contractes-i-mossos.md`.
 *
 * AVÍS: aquest fitxer exporta valors, i el backend no pot carregar aquest paquet en execució (ni a
 * PROD ni al CI). El frontend el llegeix d'aquí; el backend en té una **còpia** a
 * `apps/backend/src/modules/mossos/mossos-validity.ts`. Després de tocar res des de la línia
 * «Edat» cap avall, torna-ho a copiar allà: `mossos-validity.parity.spec.ts` falla si no són iguals.
 */

// ============================================================================
// Edat
// ============================================================================

/**
 * Edat exacta (anys complerts, amb mes i dia) el dia `today`.
 *
 * Una data `yyyy-MM-dd` (o un ISO que en comenci així) es llegeix com a **data de calendari**,
 * sense zona: `new Date('2012-12-01')` és mitjanit UTC i a Amèrica cauria el 30 de novembre.
 * Un `Date` es llegeix amb l'hora local, com ho ha fet sempre el backend (PROD: `Europe/Madrid`).
 * Sense data, o amb una data que no ho és → `null`.
 */
export function calculateAge(
  birth: string | Date | null | undefined,
  today: Date = new Date(),
): number | null {
  const parts = toCalendarParts(birth);
  if (!parts) return null;

  let age = today.getFullYear() - parts.year;
  const monthDiff = today.getMonth() + 1 - parts.month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parts.day)) age--;

  return age;
}

function toCalendarParts(
  birth: string | Date | null | undefined,
): { year: number; month: number; day: number } | null {
  if (!birth) return null;

  if (typeof birth === 'string') {
    const match = birth.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year, month, day };
  }

  if (Number.isNaN(birth.getTime())) return null;
  return { year: birth.getFullYear(), month: birth.getMonth() + 1, day: birth.getDate() };
}

/** Document obligatori a partir d'aquesta edat (RD 933/2021). */
export const MOSSOS_DOCUMENT_REQUIRED_FROM_AGE = 14;
/** Parentiu obligatori per sota d'aquesta edat. */
export const MOSSOS_MINOR_UNDER_AGE = 18;

// ============================================================================
// Tipus de document
// ============================================================================

/** Codis de tipus de document del format V24. */
export type MossosDocumentTypeCode = 'D' | 'N' | 'P' | 'O';

/** Àlies acceptats de cada codi, en majúscules. */
export const MOSSOS_DOCUMENT_TYPE_ALIASES: Record<string, MossosDocumentTypeCode> = {
  D: 'D',
  DNI: 'D',
  NIF: 'D',
  TIE: 'D',
  N: 'N',
  NIE: 'N',
  P: 'P',
  PASSPORT: 'P',
  PASAPORTE: 'P',
  PASSAPORT: 'P',
  O: 'O',
  OTRO: 'O',
  OTHER: 'O',
  ALTRES: 'O',
};

/**
 * Tipus de document amb els codis de Mossos. Buit o desconegut → `''` (no se suposa res).
 *
 * El `mapDocumentTypeV24` de la pujada (backend) encara és una mica diferent: no treu espais,
 * no coneix `PASSAPORT` ni `ALTRES` i tot el que no coneix ho envia com a `O`. Unificar-lo
 * canviaria el camp 4 del fitxer; per això la pujada passa el seu a `resolveDocumentType`.
 */
export function mossosDocumentTypeCode(type: string | null | undefined): MossosDocumentTypeCode | '' {
  const upper = (type ?? '').toString().trim().toUpperCase();
  return MOSSOS_DOCUMENT_TYPE_ALIASES[upper] ?? '';
}

// ============================================================================
// DNI i NIE: format i lletra de control
// ============================================================================

const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const NIE_PREFIX_DIGIT: Record<string, string> = { X: '0', Y: '1', Z: '2' };

export type SpanishDocumentIssue = 'EMPTY' | 'FORMAT' | 'LETTER';

export interface SpanishDocumentCheck {
  isValid: boolean;
  kind: 'DNI' | 'NIE' | 'UNKNOWN';
  /** Només si `isValid` és fals. */
  issue?: SpanishDocumentIssue;
  /** Només amb `LETTER`: la lletra que tocava. */
  expectedLetter?: string;
}

/** Treu espais, guions i punts, i passa a majúscules. */
export function normalizeSpanishDocumentNumber(document: string | null | undefined): string {
  return (document ?? '').replace(/[\s\-.]/g, '').toUpperCase();
}

/**
 * Comprova un DNI (`8 xifres + lletra`) o un NIE (`X/Y/Z + 7 xifres + lletra`): el primer
 * caràcter diu quin dels dos és. No mira el tipus declarat.
 */
export function checkSpanishDocument(document: string | null | undefined): SpanishDocumentCheck {
  const normalized = typeof document === 'string' ? normalizeSpanishDocumentNumber(document) : '';
  if (!normalized) return { isValid: false, kind: 'UNKNOWN', issue: 'EMPTY' };

  if (/^[XYZ]/.test(normalized)) {
    const match = normalized.match(/^([XYZ])(\d{7})([A-Z])$/);
    if (!match) return { isValid: false, kind: 'NIE', issue: 'FORMAT' };
    return letterCheck('NIE', NIE_PREFIX_DIGIT[match[1]] + match[2], match[3]);
  }

  if (/^\d/.test(normalized)) {
    const match = normalized.match(/^(\d{8})([A-Z])$/);
    if (!match) return { isValid: false, kind: 'DNI', issue: 'FORMAT' };
    return letterCheck('DNI', match[1], match[2]);
  }

  return { isValid: false, kind: 'UNKNOWN', issue: 'FORMAT' };
}

function letterCheck(kind: 'DNI' | 'NIE', digits: string, letter: string): SpanishDocumentCheck {
  const expectedLetter = DNI_LETTERS[parseInt(digits, 10) % 23];
  if (letter === expectedLetter) return { isValid: true, kind };
  return { isValid: false, kind, issue: 'LETTER', expectedLetter };
}

/** Passaport: com a mínim 6 caràcters alfanumèrics, sense comptar els espais. */
const PASSPORT_FORMAT = /^[A-Z0-9]{6,}$/i;

// ============================================================================
// Nacionalitat
// ============================================================================

/** Nacionalitat espanyola, en qualsevol de les formes que hi ha a la DB. Buit → fals. */
export function isSpanishNationalityValue(nationality: string | null | undefined): boolean {
  if (!nationality) return false;
  return ['ES', 'ESP', 'SPAIN', 'ESPAÑA', 'ESPANYA'].includes(nationality.toUpperCase());
}

// ============================================================================
// La regla
// ============================================================================

/**
 * Codis del que li falta o li falla a un viatger.
 *
 * | Codi | Què vol dir |
 * |---|---|
 * | `FIRST_NAME` · `LAST_NAME` | sense nom · sense cognoms |
 * | `LAST_NAME_2` | DNI amb un sol cognom |
 * | `BIRTH` | sense data de naixement |
 * | `SEX` · `NATIONALITY` | sense sexe · sense nacionalitat |
 * | `DOC_TYPE` | sense tipus de document, amb 14 anys o més o sense edat |
 * | `DOCUMENT` | sense número de document i amb 14 anys o més |
 * | `DOCUMENT_UNKNOWN_AGE` | sense número de document i sense edat (no se sap si en necessita) |
 * | `DOC_BLANK` | DNI o NIE amb un número que només té espais |
 * | `DOC_FORMAT` | DNI o NIE amb un número que no en té el format |
 * | `DOC_LETTER` | DNI o NIE amb la lletra de control dolenta (`expectedLetter`) |
 * | `PASSPORT_FORMAT` | passaport amb menys de 6 caràcters alfanumèrics |
 * | `SUPPORT` | DNI o NIE sense número de suport (IDESP) |
 * | `CONTACT` | sense telèfon ni correu |
 * | `KINSHIP` | menor de 18 sense parentiu, amb la reserva marcada com a entrada |
 * | `NIE_SPANISH` | NIE amb nacionalitat espanyola (sospitós, però possible) |
 */
export type MossosIssueCode =
  | 'FIRST_NAME'
  | 'LAST_NAME'
  | 'LAST_NAME_2'
  | 'BIRTH'
  | 'SEX'
  | 'NATIONALITY'
  | 'DOC_TYPE'
  | 'DOCUMENT'
  | 'DOCUMENT_UNKNOWN_AGE'
  | 'DOC_BLANK'
  | 'DOC_FORMAT'
  | 'DOC_LETTER'
  | 'PASSPORT_FORMAT'
  | 'SUPPORT'
  | 'CONTACT'
  | 'KINSHIP'
  | 'NIE_SPANISH';

/** `error`: la pujada automàtica exclou el viatger. `warning`: s'envia igualment. */
export type MossosIssueSeverity = 'error' | 'warning';

/**
 * Gravetat de cada codi = el que fa avui la pujada (`isBlockingForPortal`, 8-OCT-2026).
 * Decidit per en Pere (9-OCT-2026): sense data de naixement se segueix enviant (`BIRTH` és avís) i
 * l'IDESP és un avís a tot arreu (`SUPPORT`).
 */
export const MOSSOS_ISSUE_SEVERITY: Record<MossosIssueCode, MossosIssueSeverity> = {
  FIRST_NAME: 'error',
  LAST_NAME: 'error',
  DOCUMENT: 'error',
  DOC_FORMAT: 'error',
  DOC_LETTER: 'error',
  CONTACT: 'error',
  LAST_NAME_2: 'warning',
  BIRTH: 'warning',
  SEX: 'warning',
  NATIONALITY: 'warning',
  DOC_TYPE: 'warning',
  DOCUMENT_UNKNOWN_AGE: 'warning',
  DOC_BLANK: 'warning',
  PASSPORT_FORMAT: 'warning',
  SUPPORT: 'warning',
  KINSHIP: 'warning',
  NIE_SPANISH: 'warning',
};

export interface MossosTravellerIssue {
  code: MossosIssueCode;
  severity: MossosIssueSeverity;
  /** `DOC_LETTER`: la lletra de control que tocava. */
  expectedLetter?: string;
  /** `DOC_FORMAT` i `DOC_LETTER`: el tipus declarat (`D` o `N`). */
  documentType?: MossosDocumentTypeCode;
}

/**
 * El viatger tal com el mira la regla. Els buits es miren com els mira JavaScript (`!valor`):
 * qui vulgui que un text només d'espais compti com a buit, que el retalli abans.
 *
 * El contacte és l'**efectiu**: si qui crida té una cascada (viatger → persona → reserva), que
 * la resolgui abans. N'hi ha prou amb un dels tres.
 */
export interface MossosTravellerInput {
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | Date | null;
  sex?: string | null;
  nationality?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  campSuport?: string | null;
  relacioParentesc?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  /** La reserva està marcada com a entrada (`CHECKED_IN`). Només compta per a `KINSHIP`. */
  checkedIn?: boolean;
}

export interface MossosTravellerIssuesOptions {
  /**
   * Com es passa el tipus de document a codi. Per defecte, `mossosDocumentTypeCode`. La pujada
   * hi posa el seu `mapDocumentTypeV24` fins que es decideixi unificar-los.
   */
  resolveDocumentType?: (type: string | null | undefined) => MossosDocumentTypeCode | '';
}

/**
 * Tot el que li falta o li falla a un viatger, en un ordre fix (el de `MossosIssueCode`).
 * Llista buida = complet per a tothom.
 */
export function mossosTravellerIssues(
  traveller: MossosTravellerInput,
  today: Date,
  options: MossosTravellerIssuesOptions = {},
): MossosTravellerIssue[] {
  const issues: MossosTravellerIssue[] = [];
  const add = (code: MossosIssueCode, extra: Partial<MossosTravellerIssue> = {}) =>
    issues.push({ code, severity: MOSSOS_ISSUE_SEVERITY[code], ...extra });

  const resolveType = options.resolveDocumentType ?? mossosDocumentTypeCode;
  const docType = resolveType(traveller.documentType);
  const isDniOrNie = docType === 'D' || docType === 'N';
  const number = traveller.documentNumber;
  const age = calculateAge(traveller.dateOfBirth, today);
  const documentAge = age === null || age >= MOSSOS_DOCUMENT_REQUIRED_FROM_AGE;

  // Nom i cognoms
  if (!traveller.firstName) add('FIRST_NAME');
  if (!traveller.lastName) add('LAST_NAME');
  if (docType === 'D' && (traveller.lastName ?? '').trim().split(/\s+/).length < 2) add('LAST_NAME_2');

  // Dades personals
  if (!traveller.dateOfBirth) add('BIRTH');
  if (!traveller.sex) add('SEX');
  if (!traveller.nationality) add('NATIONALITY');

  // Document
  if (documentAge && !traveller.documentType) add('DOC_TYPE');
  if (!number) {
    if (age === null) add('DOCUMENT_UNKNOWN_AGE');
    else if (age >= MOSSOS_DOCUMENT_REQUIRED_FROM_AGE) add('DOCUMENT');
  } else if (isDniOrNie) {
    if (!number.trim()) {
      add('DOC_BLANK');
    } else {
      const check = checkSpanishDocument(number);
      if (check.issue === 'LETTER') {
        add('DOC_LETTER', { expectedLetter: check.expectedLetter, documentType: docType });
      } else if (!check.isValid) {
        add('DOC_FORMAT', { documentType: docType });
      }
    }
  } else if (docType === 'P' && !PASSPORT_FORMAT.test(number.replace(/\s+/g, ''))) {
    add('PASSPORT_FORMAT');
  }
  if (isDniOrNie && !traveller.campSuport) add('SUPPORT');

  // Contacte
  if (!traveller.email && !traveller.phone && !traveller.mobile) add('CONTACT');

  // Parentiu
  if (age !== null && age < MOSSOS_MINOR_UNDER_AGE && traveller.checkedIn && !traveller.relacioParentesc) {
    add('KINSHIP');
  }

  // NIE amb nacionalitat espanyola
  if (docType === 'N' && isSpanishNationalityValue(traveller.nationality)) add('NIE_SPANISH');

  return issues;
}

/** Hi ha algun codi dels demanats? */
export function hasMossosIssue(issues: MossosTravellerIssue[], ...codes: MossosIssueCode[]): boolean {
  return issues.some((issue) => codes.includes(issue.code));
}

/** Algun codi fa que la pujada automàtica exclogui el viatger? */
export function blocksMossosUpload(issues: MossosTravellerIssue[]): boolean {
  return issues.some((issue) => issue.severity === 'error');
}
